import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DirhamIconColored, PriceWithDirham } from '../components/DirhamIcon';
import { ErrorDisplay, showErrorToast } from '../components/ErrorDisplay';
import { Upload, Globe, CloudUpload, Check, X, RefreshCw, Trash2, AlertTriangle, Clock, Shield, Calendar, Users, Fingerprint, Smartphone, Copy } from 'lucide-react';
import { 
  isWebAuthnSupported, 
  isPlatformAuthenticatorAvailable,
  registerBiometric,
  deleteCredential
} from '../utils/webauthn';

const Settings = () => {
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const { isAdmin, isBarber, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [driveStatus, setDriveStatus] = useState({ connected: false, last_backup: null });
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState('');
  const [otpData, setOtpData] = useState(null);
  const [otpInput, setOtpInput] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [sectionNames, setSectionNames] = useState({
    revenues: 'الإيرادات',
    expenses: 'المصروفات',
    invoices: 'الفواتير'
  });
  const [currencySettings, setCurrencySettings] = useState({
    symbol: 'د.إ',
    name: 'درهم إماراتي',
    position: 'after' // 'before' or 'after'
  });
  const [queueSettings, setQueueSettings] = useState({
    queue_retention_days: 60,
    min_booking_hours: 2,
    max_booking_days: 7
  });
  
  // Social Media Links
  const [socialMediaLinks, setSocialMediaLinks] = useState({
    instagram: '',
    tiktok: '',
    snapchat: '',
    facebook: '',
    twitter: '',
    youtube: ''
  });
  
  // Deposit Settings
  const [depositSettings, setDepositSettings] = useState({
    deposit_amount: 50,
    deposit_type: 'fixed',
    deposit_percentage: 20,
    is_refundable: false,
    deduct_from_total: true
  });
  const [depositProposals, setDepositProposals] = useState([]);
  
  // Timezone Check Settings
  const [timezoneSettings, setTimezoneSettings] = useState({
    timezone_check_enabled: true,
    timezone_max_difference_minutes: 45,
    require_uae_for_data_entry: true
  });

  // Date Display Settings (Gregorian + Hijri beside header clock)
  const [dateDisplaySettings, setDateDisplaySettings] = useState({
    show_gregorian: true,
    show_hijri: true,
    hijri_offset_days: 0
  });
  const [dateDisplaySaving, setDateDisplaySaving] = useState(false);

  // Biometric Settings
  const [biometricStatus, setBiometricStatus] = useState({
    supported: false,
    platformAvailable: false,
    registered: false,
    credentialId: null
  });
  const [biometricLoading, setBiometricLoading] = useState(false);

  // Trial Period Management
  const [showExtendTrialDialog, setShowExtendTrialDialog] = useState(false);
  const [showSetDateDialog, setShowSetDateDialog] = useState(false);
  const [showCancelTrialDialog, setShowCancelTrialDialog] = useState(false);
  const [extendDays, setExtendDays] = useState(30);
  const [newTrialEndDate, setNewTrialEndDate] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [trialLoading, setTrialLoading] = useState(false);
  const [cancelKeepData, setCancelKeepData] = useState(true); // true = الاحتفاظ بالبيانات، false = إعادة التطبيق فارغ

  useEffect(() => {
    if (isAdmin()) {
      fetchData();
      fetchSocialMediaLinks();
      fetchDepositSettings();
      fetchDateDisplaySettings();
    } else if (isBarber()) {
      // Barber can access deposit settings only
      fetchDepositSettings();
    }
    // Check biometric availability
    checkBiometricStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkBiometricStatus = async () => {
    const supported = isWebAuthnSupported();
    const platformAvailable = await isPlatformAuthenticatorAvailable();
    
    let registered = false;
    let credentialId = null;
    
    if (supported && user?.id) {
      try {
        const response = await api.get('/webauthn/status');
        if (response.data.has_credential) {
          registered = true;
          credentialId = response.data.credential_id;
        }
      } catch (e) {
        console.log('WebAuthn status check failed:', e);
      }
    }
    
    setBiometricStatus({
      supported,
      platformAvailable,
      registered,
      credentialId
    });
  };

  const handleRegisterBiometric = async () => {
    if (!user?.id) {
      toast.error('يجب تسجيل الدخول أولاً');
      return;
    }
    
    setBiometricLoading(true);
    try {
      const result = await registerBiometric(user.id);
      if (result.success) {
        toast.success('تم تسجيل البصمة بنجاح! يمكنك الآن تسجيل الدخول بالبصمة.');
        checkBiometricStatus();
      } else {
        showErrorToast(result.error || 'فشل تسجيل البصمة', 'خطأ في تسجيل البصمة');
      }
    } catch (error) {
      console.error('Biometric registration error:', error);
      showErrorToast(error, 'خطأ في تسجيل البصمة');
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleDeleteBiometric = async () => {
    if (!biometricStatus.credentialId) {
      toast.error('لا توجد بصمة مسجلة');
      return;
    }
    
    setBiometricLoading(true);
    try {
      const result = await deleteCredential(biometricStatus.credentialId);
      if (result.success) {
        toast.success('تم حذف البصمة بنجاح');
        checkBiometricStatus();
      } else {
        showErrorToast(result.error || 'فشل حذف البصمة', 'خطأ في حذف البصمة');
      }
    } catch (error) {
      console.error('Biometric delete error:', error);
      showErrorToast(error, 'خطأ في حذف البصمة');
    } finally {
      setBiometricLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [settingsRes, driveRes, trialRes] = await Promise.all([
        api.get('/settings'),
        api.get('/drive/status'),
        api.get('/trial-status')
      ]);
      setSettings(settingsRes.data);
      setDriveStatus(driveRes.data);
      setTrialStatus(trialRes.data);
      if (settingsRes.data.section_names) {
        setSectionNames(settingsRes.data.section_names);
      }
      if (settingsRes.data.currency_settings) {
        setCurrencySettings(settingsRes.data.currency_settings);
      }
      if (settingsRes.data.queue_retention_days !== undefined) {
        setQueueSettings({
          queue_retention_days: settingsRes.data.queue_retention_days || 60,
          min_booking_hours: settingsRes.data.min_booking_hours || 2,
          max_booking_days: settingsRes.data.max_booking_days || 7
        });
      }
      // Load timezone settings
      setTimezoneSettings({
        timezone_check_enabled: settingsRes.data.timezone_check_enabled !== false,
        timezone_max_difference_minutes: settingsRes.data.timezone_max_difference_minutes || 45,
        require_uae_for_data_entry: settingsRes.data.require_uae_for_data_entry !== false
      });
    } catch (error) {
      toast.error('فشل تحميل الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const fetchSocialMediaLinks = async () => {
    try {
      const res = await api.get('/social-media/links');
      setSocialMediaLinks(res.data);
    } catch (error) {
      console.error('Error fetching social media links:', error);
    }
  };

  const fetchDepositSettings = async () => {
    try {
      const res = await api.get('/payments/deposit-settings');
      setDepositSettings(res.data);
      
      // Fetch proposals if admin
      if (isAdmin()) {
        const proposalsRes = await api.get('/payments/deposit-proposals');
        setDepositProposals(proposalsRes.data);
      }
    } catch (error) {
      console.error('Error fetching deposit settings:', error);
    }
  };

  const handleSaveSocialMedia = async () => {
    try {
      await api.put('/social-media/links', socialMediaLinks);
      toast.success(language === 'ar' ? 'تم حفظ روابط التواصل الاجتماعي' : 'Social media links saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل حفظ الروابط' : 'Failed to save links'));
    }
  };

  const handleSaveDepositSettings = async () => {
    try {
      const res = await api.put('/payments/deposit-settings', depositSettings);
      if (res.data.status === 'pending') {
        toast.success('تم إرسال اقتراحك للمدير للموافقة');
      } else {
        toast.success(language === 'ar' ? 'تم حفظ إعدادات العربون' : 'Deposit settings saved');
      }
      fetchDepositSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل حفظ الإعدادات' : 'Failed to save settings'));
    }
  };

  const handleApproveDepositProposal = async (proposalId) => {
    try {
      await api.put(`/payments/deposit-proposals/${proposalId}/approve`);
      toast.success('تم اعتماد الاقتراح');
      fetchDepositSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل اعتماد الاقتراح');
    }
  };

  const handleRejectDepositProposal = async (proposalId) => {
    try {
      await api.put(`/payments/deposit-proposals/${proposalId}/reject`);
      toast.success('تم رفض الاقتراح');
      fetchDepositSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل رفض الاقتراح');
    }
  };

  // Trial Period Management Handlers
  const handleExtendTrial = async () => {
    if (extendDays <= 0) {
      toast.error('يرجى إدخال عدد أيام صحيح');
      return;
    }
    
    try {
      setTrialLoading(true);
      const res = await api.post('/trial/extend', { days: extendDays });
      toast.success(res.data.message);
      setShowExtendTrialDialog(false);
      setExtendDays(30);
      // Refresh trial status
      const trialRes = await api.get('/trial-status');
      setTrialStatus(trialRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تمديد الفترة التجريبية');
    } finally {
      setTrialLoading(false);
    }
  };

  const handleSetTrialDate = async () => {
    if (!newTrialEndDate) {
      toast.error('يرجى اختيار تاريخ');
      return;
    }
    
    try {
      setTrialLoading(true);
      const res = await api.post('/trial/set-date', { end_date: newTrialEndDate });
      toast.success(res.data.message);
      setShowSetDateDialog(false);
      setNewTrialEndDate('');
      // Refresh trial status
      const trialRes = await api.get('/trial-status');
      setTrialStatus(trialRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تعيين التاريخ');
    } finally {
      setTrialLoading(false);
    }
  };

  const handleCancelTrial = async () => {
    try {
      setTrialLoading(true);
      const res = await api.post('/trial/cancel', { 
        reason: cancelReason,
        keep_data: cancelKeepData
      });
      toast.success(res.data.message);
      setShowCancelTrialDialog(false);
      setCancelReason('');
      setCancelKeepData(true);
      // Refresh trial status
      const trialRes = await api.get('/trial-status');
      setTrialStatus(trialRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إلغاء الفترة التجريبية');
    } finally {
      setTrialLoading(false);
    }
  };

  const handleRestoreTrial = async () => {
    try {
      setTrialLoading(true);
      const res = await api.post('/trial/restore');
      toast.success(res.data.message);
      // Refresh trial status
      const trialRes = await api.get('/trial-status');
      setTrialStatus(trialRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل استعادة الفترة التجريبية');
    } finally {
      setTrialLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'ar' ? 'حجم الملف يجب أن يكون أقل من 5MB' : 'File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLogoUploading(true);
      const res = await api.post('/settings/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message || (language === 'ar' ? 'تم رفع الشعار بنجاح' : 'Logo uploaded successfully'));
      
      // Force reload the logo image
      const logoElements = document.querySelectorAll('img[src*="logo"]');
      logoElements.forEach(img => {
        img.src = img.src.split('?')[0] + '?t=' + Date.now();
      });
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل رفع الشعار' : 'Failed to upload logo'));
    } finally {
      setLogoUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleConnectDrive = async () => {
    try {
      const res = await api.get('/drive/connect');
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل الاتصال بـ Google Drive');
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await api.post('/drive/backup');
      toast.success('تم النسخ الاحتياطي بنجاح');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل النسخ الاحتياطي');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleSaveSectionNames = async () => {
    try {
      await api.put('/settings', {
        section_names: sectionNames,
        currency_settings: currencySettings,
        logo_url: settings?.logo_url,
        auto_backup_enabled: settings?.auto_backup_enabled || false
      });
      toast.success('تم حفظ التغييرات بنجاح');
      fetchData();
    } catch (error) {
      toast.error('فشل حفظ التغييرات');
    }
  };

  const handleSaveCurrencySettings = async () => {
    try {
      await api.put('/settings', {
        section_names: sectionNames,
        currency_settings: currencySettings,
        logo_url: settings?.logo_url,
        auto_backup_enabled: settings?.auto_backup_enabled || false,
        queue_retention_days: queueSettings.queue_retention_days,
        min_booking_hours: queueSettings.min_booking_hours,
        max_booking_days: queueSettings.max_booking_days
      });
      toast.success('تم حفظ إعدادات العملة بنجاح');
      fetchData();
    } catch (error) {
      toast.error('فشل حفظ إعدادات العملة');
    }
  };

  const handleSaveQueueSettings = async () => {
    try {
      await api.put('/settings', {
        section_names: sectionNames,
        currency_settings: currencySettings,
        logo_url: settings?.logo_url,
        auto_backup_enabled: settings?.auto_backup_enabled || false,
        queue_retention_days: queueSettings.queue_retention_days,
        min_booking_hours: queueSettings.min_booking_hours,
        max_booking_days: queueSettings.max_booking_days
      });
      toast.success('تم حفظ إعدادات الحجز بنجاح');
      fetchData();
    } catch (error) {
      toast.error('فشل حفظ إعدادات الحجز');
    }
  };

  const handleCleanupQueue = async () => {
    try {
      const res = await api.post('/admin/cleanup-queue');
      toast.success(res.data.message);
    } catch (error) {
      toast.error('فشل تنظيف قائمة الانتظار');
    }
  };

  const handleSaveTimezoneSettings = async () => {
    try {
      await api.put('/settings', {
        ...settings,
        timezone_check_enabled: timezoneSettings.timezone_check_enabled,
        timezone_max_difference_minutes: timezoneSettings.timezone_max_difference_minutes,
        require_uae_for_data_entry: timezoneSettings.require_uae_for_data_entry
      });
      toast.success('تم حفظ إعدادات فحص التوقيت بنجاح');
      fetchData();
    } catch (error) {
      toast.error('فشل حفظ إعدادات فحص التوقيت');
    }
  };

  const fetchDateDisplaySettings = async () => {
    try {
      const res = await api.get('/settings/datetime-display');
      if (res?.data) {
        setDateDisplaySettings({
          show_gregorian: res.data.show_gregorian !== false,
          show_hijri: res.data.show_hijri !== false,
          hijri_offset_days: Number(res.data.hijri_offset_days) || 0
        });
      }
    } catch (error) {
      // Endpoint is public; failure means backend is down — silently fall back to defaults.
      console.debug('[Settings] date-display public endpoint unreachable, using defaults:', error?.message);
    }
  };

  const handleSaveDateDisplaySettings = async () => {
    setDateDisplaySaving(true);
    try {
      const offset = Math.max(-2, Math.min(2, Number(dateDisplaySettings.hijri_offset_days) || 0));
      await api.put('/settings/datetime-display', {
        show_gregorian: !!dateDisplaySettings.show_gregorian,
        show_hijri: !!dateDisplaySettings.show_hijri,
        hijri_offset_days: offset
      });
      setDateDisplaySettings(prev => ({ ...prev, hijri_offset_days: offset }));
      toast.success('تم حفظ إعدادات عرض التاريخ بنجاح');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'فشل حفظ إعدادات عرض التاريخ');
    } finally {
      setDateDisplaySaving(false);
    }
  };

  // Delete Data Functions
  const handleRequestOTP = async (action) => {
    setDeleteAction(action);
    setDeleteLoading(true);
    try {
      const res = await api.post(`/admin/request-otp?action=${action}`);
      setOtpData(res.data);
      setOtpInput('');
      toast.success('تم إنشاء رمز التحقق');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إنشاء رمز التحقق');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleVerifyAndDelete = async () => {
    if (!otpInput || otpInput.length !== 6) {
      toast.error('يرجى إدخال رمز التحقق المكون من 6 أرقام');
      return;
    }

    setDeleteLoading(true);
    try {
      const res = await api.post('/admin/verify-otp-and-delete', {
        otp: otpInput,
        action: deleteAction
      });
      toast.success(res.data.message);
      setDeleteDialogOpen(false);
      setOtpData(null);
      setOtpInput('');
      setDeleteAction('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل التحقق أو الحذف');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'delete_all_barbers': 'حذف جميع الحلاقين وإيراداتهم',
      'delete_all_revenues': 'حذف جميع الإيرادات',
      'delete_all_data': 'حذف جميع البيانات (ماعدا المستخدمين)'
    };
    return labels[action] || action;
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 relative z-0 pb-20">
      <div>
        <h1 className="text-3xl font-bold">{t('settings')}</h1>
        <p className="text-muted-foreground">إعدادات النظام</p>
      </div>

      {/* Trial Period Section */}
      {trialStatus && (
        <div className={`bg-gradient-to-r ${trialStatus.is_expired || trialStatus.is_cancelled ? 'from-red-500/20 to-red-500/5 border-red-500/30' : 'from-primary/20 to-primary/5 border-primary/30'} border rounded-xl shadow-2xl p-6`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Clock className={`w-6 h-6 ${trialStatus.is_expired || trialStatus.is_cancelled ? 'text-red-500' : 'text-primary'}`} />
              <h3 className="text-xl font-semibold">فترة التجربة</h3>
            </div>
            {trialStatus.is_cancelled && (
              <span className="px-3 py-1 bg-red-500/20 text-red-500 rounded-full text-sm font-medium">
                ملغاة
              </span>
            )}
          </div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <p className={`text-3xl font-bold ${trialStatus.is_expired || trialStatus.is_cancelled ? 'text-red-500' : 'text-primary'}`}>
                {trialStatus.is_cancelled ? 'ملغاة' : `${trialStatus.days_remaining} يوم متبقي`}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {trialStatus.is_cancelled 
                  ? `السبب: ${trialStatus.cancel_reason || 'غير محدد'}`
                  : `تنتهي في: ${new Date(trialStatus.trial_end).toLocaleDateString('ar-SA')}`
                }
              </p>
            </div>
            <div className="text-sm text-muted-foreground">
              <p>بداية الفترة: {new Date(trialStatus.trial_start).toLocaleDateString('ar-SA')}</p>
              <p>نهاية الفترة: {new Date(trialStatus.trial_end).toLocaleDateString('ar-SA')}</p>
            </div>
          </div>
          
          {/* Trial Management Actions - Admin Only */}
          {isAdmin() && (
            <div className="border-t border-white/10 pt-4">
              <h4 className="text-sm font-medium text-muted-foreground mb-3">إدارة الفترة التجريبية</h4>
              <div className="flex flex-wrap gap-3">
                {/* Extend Trial */}
                <Dialog open={showExtendTrialDialog} onOpenChange={setShowExtendTrialDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-2"
                      data-testid="extend-trial-btn"
                      disabled={trialStatus.is_cancelled}
                    >
                      <Calendar className="w-4 h-4" />
                      تمديد الفترة
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-white/10">
                    <DialogHeader>
                      <DialogTitle>تمديد الفترة التجريبية</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <label className="text-sm text-muted-foreground">عدد الأيام للتمديد</label>
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          value={extendDays}
                          onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                          className="mt-1"
                          data-testid="extend-days-input"
                        />
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">التاريخ الجديد:</span>
                        <span className="font-medium">
                          {new Date(new Date(trialStatus.trial_end).getTime() + extendDays * 24 * 60 * 60 * 1000).toLocaleDateString('ar-SA')}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setShowExtendTrialDialog(false)}>
                        إلغاء
                      </Button>
                      <Button 
                        onClick={handleExtendTrial} 
                        disabled={trialLoading || extendDays <= 0}
                        data-testid="confirm-extend-btn"
                      >
                        {trialLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'تمديد'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Set Specific Date */}
                <Dialog open={showSetDateDialog} onOpenChange={setShowSetDateDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-2"
                      data-testid="set-date-btn"
                      disabled={trialStatus.is_cancelled}
                    >
                      <Clock className="w-4 h-4" />
                      تحديد تاريخ
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-white/10">
                    <DialogHeader>
                      <DialogTitle>تحديد تاريخ انتهاء الفترة</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <label className="text-sm text-muted-foreground">تاريخ الانتهاء الجديد</label>
                        <Input
                          type="date"
                          value={newTrialEndDate}
                          onChange={(e) => setNewTrialEndDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="mt-1"
                          data-testid="new-end-date-input"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setShowSetDateDialog(false)}>
                        إلغاء
                      </Button>
                      <Button 
                        onClick={handleSetTrialDate} 
                        disabled={trialLoading || !newTrialEndDate}
                        data-testid="confirm-set-date-btn"
                      >
                        {trialLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'تأكيد'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Cancel/Restore Trial */}
                {trialStatus.is_cancelled ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="gap-2 text-green-500 border-green-500/30 hover:bg-green-500/10"
                    onClick={handleRestoreTrial}
                    disabled={trialLoading}
                    data-testid="restore-trial-btn"
                  >
                    <Check className="w-4 h-4" />
                    استعادة
                  </Button>
                ) : (
                  <Dialog open={showCancelTrialDialog} onOpenChange={setShowCancelTrialDialog}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2 text-red-500 border-red-500/30 hover:bg-red-500/10"
                        data-testid="cancel-trial-btn"
                      >
                        <X className="w-4 h-4" />
                        إلغاء الفترة
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-white/10">
                      <DialogHeader>
                        <DialogTitle className="text-red-500">إلغاء الفترة التجريبية</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                          <p className="text-red-500 text-sm">⚠️ سيتم إلغاء الفترة التجريبية فوراً.</p>
                        </div>
                        
                        {/* خيار الاحتفاظ بالبيانات */}
                        <div className="space-y-3">
                          <label className="text-sm font-medium">ماذا تريد أن يحدث للبيانات؟</label>
                          <div className="space-y-2">
                            <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 cursor-pointer hover:bg-secondary/30 transition-colors">
                              <input
                                type="radio"
                                name="keep-data"
                                checked={cancelKeepData === true}
                                onChange={() => setCancelKeepData(true)}
                                className="w-4 h-4 accent-primary"
                              />
                              <div>
                                <span className="font-medium text-green-500">الاحتفاظ بالبيانات</span>
                                <p className="text-xs text-muted-foreground">سيتم حفظ جميع البيانات (الإيرادات، المصروفات، الحجوزات...)</p>
                              </div>
                            </label>
                            <label className="flex items-center gap-3 p-3 rounded-lg border border-red-500/30 cursor-pointer hover:bg-red-500/10 transition-colors">
                              <input
                                type="radio"
                                name="keep-data"
                                checked={cancelKeepData === false}
                                onChange={() => setCancelKeepData(false)}
                                className="w-4 h-4 accent-red-500"
                              />
                              <div>
                                <span className="font-medium text-red-500">إعادة التطبيق فارغ</span>
                                <p className="text-xs text-muted-foreground">سيتم حذف جميع البيانات نهائياً (لا يمكن التراجع)</p>
                              </div>
                            </label>
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-sm text-muted-foreground">سبب الإلغاء (اختياري)</label>
                          <Input
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="أدخل سبب الإلغاء..."
                            className="mt-1"
                            data-testid="cancel-reason-input"
                          />
                        </div>
                        
                        {!cancelKeepData && (
                          <div className="p-3 bg-red-500/20 rounded-lg border border-red-500/40">
                            <p className="text-red-400 text-sm font-medium">⚠️ تحذير: اختيار "إعادة التطبيق فارغ" سيحذف جميع البيانات نهائياً!</p>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowCancelTrialDialog(false)}>
                          تراجع
                        </Button>
                        <Button 
                          variant="destructive"
                          onClick={handleCancelTrial}
                          disabled={trialLoading}
                          data-testid="confirm-cancel-trial-btn"
                        >
                          {trialLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : (cancelKeepData ? 'إلغاء الفترة' : 'حذف البيانات وإلغاء الفترة')}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
          )}
          
          {trialStatus.is_expired && !trialStatus.is_cancelled && (
            <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
              <p className="text-red-500 text-sm">⚠️ انتهت فترة التجربة. يرجى التواصل للتفعيل الكامل.</p>
            </div>
          )}
        </div>
      )}

      {/* Logo Section */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Upload className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">{language === 'ar' ? 'شعار الصالون' : 'Salon Logo'}</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Current Logo Preview */}
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-muted-foreground">{language === 'ar' ? 'الشعار الحالي' : 'Current Logo'}</p>
            <div className="w-40 h-40 bg-black rounded-xl flex items-center justify-center border-2 border-primary/30 shadow-lg">
              <img 
                src="/logo-gold.png" 
                alt="Logo" 
                className="max-w-[80%] max-h-[80%] object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              {language === 'ar' ? 'يظهر في الصفحة الرئيسية والتقارير وأيقونات التطبيق' : 'Appears on homepage, reports, and app icons'}
            </p>
          </div>
          
          {/* Upload Section */}
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-muted-foreground">{language === 'ar' ? 'رفع شعار جديد' : 'Upload New Logo'}</p>
            <div className="w-40 h-40 bg-secondary/30 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-white/20 hover:border-primary/50 transition-colors cursor-pointer relative group"
                 onClick={() => document.getElementById('logo-upload').click()}>
              <Upload className="w-10 h-10 text-muted-foreground group-hover:text-primary transition-colors mb-2" />
              <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors">
                {language === 'ar' ? 'اضغط للرفع' : 'Click to upload'}
              </span>
              {logoUploading && (
                <div className="absolute inset-0 bg-black/70 rounded-xl flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
            <input
              id="logo-upload"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleLogoUpload}
              className="hidden"
            />
            <div className="text-center">
              <p className="text-xs text-muted-foreground">PNG, JPG, WEBP</p>
              <p className="text-xs text-muted-foreground">{language === 'ar' ? 'الحد الأقصى 5MB' : 'Max 5MB'}</p>
            </div>
          </div>
        </div>
        
        {/* Info Box */}
        <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
          <p className="text-sm text-primary font-medium mb-2">
            {language === 'ar' ? '💡 ملاحظة' : '💡 Note'}
          </p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• {language === 'ar' ? 'الشعار سيظهر في الصفحة الرئيسية وصفحة تسجيل الدخول' : 'Logo will appear on homepage and login page'}</li>
            <li>• {language === 'ar' ? 'سيتم تحديث أيقونات التطبيق (Android/iPhone) تلقائياً' : 'App icons (Android/iPhone) will be updated automatically'}</li>
            <li>• {language === 'ar' ? 'يُفضل استخدام صورة بخلفية شفافة للحصول على أفضل نتيجة' : 'Transparent background recommended for best results'}</li>
          </ul>
        </div>
      </div>

      {/* Language Section */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">{t('language')}</h3>
        </div>
        
        <div className="flex items-center gap-4">
          <Button
            data-testid="toggle-language-btn-settings"
            onClick={toggleLanguage}
            variant="outline"
            className="border-white/10"
          >
            <Globe className="w-4 h-4 me-2" />
            {language === 'ar' ? 'التبديل إلى English' : 'Switch to العربية'}
          </Button>
          <p className="text-sm text-muted-foreground">
            اللغة الحالية: {language === 'ar' ? 'العربية' : 'English'}
          </p>
        </div>
      </div>

      {/* Section Names Customization */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
        <h3 className="text-xl font-semibold mb-6">{t('customizeSections')}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">اسم قسم الإيرادات</label>
            <Input
              data-testid="section-revenues-input"
              value={sectionNames.revenues}
              onChange={(e) => setSectionNames({ ...sectionNames, revenues: e.target.value })}
              className="bg-secondary/50 border-white/10"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">اسم قسم المصروفات</label>
            <Input
              data-testid="section-expenses-input"
              value={sectionNames.expenses}
              onChange={(e) => setSectionNames({ ...sectionNames, expenses: e.target.value })}
              className="bg-secondary/50 border-white/10"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">اسم قسم الفواتير</label>
            <Input
              data-testid="section-invoices-input"
              value={sectionNames.invoices}
              onChange={(e) => setSectionNames({ ...sectionNames, invoices: e.target.value })}
              className="bg-secondary/50 border-white/10"
            />
          </div>
          <Button
            data-testid="save-sections-btn"
            onClick={handleSaveSectionNames}
            className="bg-primary text-primary-foreground"
          >
            {t('save')}
          </Button>
        </div>
      </div>

      {/* Currency Settings */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <DirhamIconColored className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">إعدادات العملة</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">رمز العملة</label>
            <Input
              data-testid="currency-symbol-input"
              value={currencySettings.symbol}
              onChange={(e) => setCurrencySettings({ ...currencySettings, symbol: e.target.value })}
                            placeholder="رمز العملة (اختياري)"
              className="bg-secondary/50 border-white/10"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">اسم العملة</label>
            <Input
              data-testid="currency-name-input"
              value={currencySettings.name}
              onChange={(e) => setCurrencySettings({ ...currencySettings, name: e.target.value })}
              placeholder="مثال: درهم إماراتي"
              className="bg-secondary/50 border-white/10"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">موضع الرمز</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="currency-position"
                  checked={currencySettings.position === 'before'}
                  onChange={() => setCurrencySettings({ ...currencySettings, position: 'before' })}
                  className="w-4 h-4 accent-primary"
                />
                <span>قبل الرقم ($ 100)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="currency-position"
                  checked={currencySettings.position === 'after'}
                  onChange={() => setCurrencySettings({ ...currencySettings, position: 'after' })}
                  className="w-4 h-4 accent-primary"
                />
                                <span>بعد الرقم (100 <DirhamIconColored className="w-3 h-3 inline" />)</span>
              </label>
            </div>
          </div>
          <div className="p-4 bg-secondary/30 rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">معاينة:</p>
            <p className="text-xl font-bold text-primary inline-flex items-center gap-1">
              {currencySettings.position === 'before' ? (
                <>
                  <DirhamIconColored className="w-5 h-5" color="primary" />
                  <span>1,500</span>
                </>
              ) : (
                <>
                  <span>1,500</span>
                  <DirhamIconColored className="w-5 h-5" color="primary" />
                </>
              )}
            </p>
          </div>
          <Button
            data-testid="save-currency-btn"
            onClick={handleSaveCurrencySettings}
            className="bg-primary text-primary-foreground"
          >
            حفظ إعدادات العملة
          </Button>
        </div>
      </div>

      {/* Queue/Booking Settings */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Calendar className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">إعدادات الحجز وقائمة الانتظار</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">مدة الاحتفاظ بالحجوزات (بالأيام)</label>
            <Input
              data-testid="queue-retention-input"
              type="number"
              value={queueSettings.queue_retention_days}
              onChange={(e) => setQueueSettings({ ...queueSettings, queue_retention_days: parseInt(e.target.value) || 60 })}
              min={1}
              max={365}
              className="bg-secondary/50 border-white/10"
            />
            <p className="text-xs text-muted-foreground mt-1">
              سيتم حذف الحجوزات تلقائياً بعد هذه المدة (الافتراضي: 60 يوم = شهرين)
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">الحد الأدنى للحجز (بالساعات قبل الموعد)</label>
            <Input
              data-testid="min-booking-hours-input"
              type="number"
              value={queueSettings.min_booking_hours}
              onChange={(e) => setQueueSettings({ ...queueSettings, min_booking_hours: parseInt(e.target.value) || 2 })}
              min={1}
              max={48}
              className="bg-secondary/50 border-white/10"
            />
            <p className="text-xs text-muted-foreground mt-1">
              لا يمكن للعميل الحجز قبل أقل من هذه المدة (الافتراضي: 2 ساعات)
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">الحد الأقصى للحجز (بالأيام من اليوم)</label>
            <Input
              data-testid="max-booking-days-input"
              type="number"
              value={queueSettings.max_booking_days}
              onChange={(e) => setQueueSettings({ ...queueSettings, max_booking_days: parseInt(e.target.value) || 7 })}
              min={1}
              max={30}
              className="bg-secondary/50 border-white/10"
            />
            <p className="text-xs text-muted-foreground mt-1">
              لا يمكن للعميل الحجز لأكثر من هذه المدة (الافتراضي: 7 أيام = أسبوع)
            </p>
          </div>

          <div className="p-4 bg-secondary/30 rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">ملخص الإعدادات:</p>
            <ul className="text-sm space-y-1">
              <li>• الحجوزات تُحذف بعد: <span className="text-primary font-bold">{queueSettings.queue_retention_days} يوم</span></li>
              <li>• الحجز قبل: <span className="text-primary font-bold">{queueSettings.min_booking_hours} ساعات على الأقل</span></li>
              <li>• الحجز خلال: <span className="text-primary font-bold">{queueSettings.max_booking_days} أيام كحد أقصى</span></li>
            </ul>
          </div>

          <div className="flex gap-4">
            <Button
              data-testid="save-queue-settings-btn"
              onClick={handleSaveQueueSettings}
              className="bg-primary text-primary-foreground"
            >
              حفظ إعدادات الحجز
            </Button>
            <Button
              data-testid="cleanup-queue-btn"
              onClick={handleCleanupQueue}
              variant="outline"
              className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
            >
              <Trash2 className="w-4 h-4 me-2" />
              تنظيف الحجوزات المنتهية
            </Button>
          </div>
        </div>
      </div>

      {/* Timezone Security Settings */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6 relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">إعدادات الأمان والتوقيت</h3>
        </div>
        
        <div className="space-y-6">
          <div className="p-4 bg-secondary/30 rounded-lg border border-primary/20">
            <p className="text-sm text-muted-foreground mb-4">
              هذه الميزة تمنع تسجيل الدخول من خارج الإمارات بناءً على توقيت جهاز المستخدم.
              المدير فقط يمكنه تسجيل الدخول من خارج الإمارات مع تحذير.
            </p>
            
            <div className="flex items-center justify-between mb-4">
              <label className="font-medium">تفعيل فحص التوقيت</label>
              <button
                onClick={() => setTimezoneSettings(prev => ({ ...prev, timezone_check_enabled: !prev.timezone_check_enabled }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  timezoneSettings.timezone_check_enabled ? 'bg-primary' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    timezoneSettings.timezone_check_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {timezoneSettings.timezone_check_enabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    أقصى فرق مسموح بالدقائق (الافتراضي: 45)
                  </label>
                  <Input
                    type="number"
                    value={timezoneSettings.timezone_max_difference_minutes}
                    onChange={(e) => setTimezoneSettings(prev => ({ 
                      ...prev, 
                      timezone_max_difference_minutes: parseInt(e.target.value) || 45 
                    }))}
                    className="bg-secondary/50 border-white/10 w-32"
                    min="15"
                    max="180"
                  />
                </div>
                
                {/* منع الإدراج من خارج الإمارات */}
                <div className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div>
                    <label className="font-medium">منع إدراج البيانات من خارج الإمارات</label>
                    <p className="text-xs text-muted-foreground mt-1">
                      لن يستطيع أي مستخدم (عدا المدير) إضافة إيرادات أو مصاريف من خارج الإمارات
                    </p>
                  </div>
                  <button
                    onClick={() => setTimezoneSettings(prev => ({ ...prev, require_uae_for_data_entry: !prev.require_uae_for_data_entry }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      timezoneSettings.require_uae_for_data_entry ? 'bg-red-500' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        timezoneSettings.require_uae_for_data_entry ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-500 mb-1">ملاحظة:</p>
                <p className="text-muted-foreground">
                  • المستخدمون خارج الإمارات سيرون رسالة "تسجيل الدخول غير مسموح من خارج الإمارات"
                </p>
                <p className="text-muted-foreground">
                  • المدير يمكنه تسجيل الدخول من أي مكان مع تحذير
                </p>
              </div>
            </div>
          </div>

          <Button
            data-testid="save-timezone-settings-btn"
            onClick={handleSaveTimezoneSettings}
            className="bg-primary text-primary-foreground"
          >
            حفظ إعدادات التوقيت
          </Button>
        </div>
      </div>

      {/* Date Display Settings (Gregorian + Hijri header clock) */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6 relative z-10" data-testid="date-display-settings-card">
        <div className="flex items-center gap-3 mb-6">
          <Calendar className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">إعدادات عرض التاريخ بجانب الساعة</h3>
        </div>

        <div className="space-y-6">
          <div className="p-4 bg-secondary/30 rounded-lg border border-primary/20 space-y-4">
            <p className="text-sm text-muted-foreground">
              تتحكم في إظهار التاريخ الميلادي والهجري بجانب الساعة في رأس الصفحة لجميع المستخدمين.
              يمكنك تعديل اليوم الهجري بفارق من -2 إلى +2 لمطابقة الرؤية الشرعية المحلية إذا لزم الأمر.
            </p>

            {/* Show Gregorian */}
            <div className="flex items-center justify-between">
              <label className="font-medium">إظهار التاريخ الميلادي</label>
              <button
                type="button"
                data-testid="toggle-show-gregorian"
                onClick={() => setDateDisplaySettings(prev => ({ ...prev, show_gregorian: !prev.show_gregorian }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  dateDisplaySettings.show_gregorian ? 'bg-primary' : 'bg-gray-600'
                }`}
                aria-label="إظهار التاريخ الميلادي"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    dateDisplaySettings.show_gregorian ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Show Hijri */}
            <div className="flex items-center justify-between">
              <label className="font-medium">إظهار التاريخ الهجري</label>
              <button
                type="button"
                data-testid="toggle-show-hijri"
                onClick={() => setDateDisplaySettings(prev => ({ ...prev, show_hijri: !prev.show_hijri }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  dateDisplaySettings.show_hijri ? 'bg-amber-500' : 'bg-gray-600'
                }`}
                aria-label="إظهار التاريخ الهجري"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    dateDisplaySettings.show_hijri ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Hijri offset */}
            {dateDisplaySettings.show_hijri && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-2">
                <label className="block text-sm font-medium">
                  تعديل اليوم الهجري (فرق بالأيام)
                </label>
                <p className="text-xs text-muted-foreground">
                  استخدم قيمة سالبة (-1 / -2) لإرجاع الهجري يوماً أو يومين، أو قيمة موجبة (+1 / +2) لتقديمه.
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    data-testid="hijri-offset-minus"
                    variant="outline"
                    className="border-white/10"
                    onClick={() => setDateDisplaySettings(prev => ({
                      ...prev,
                      hijri_offset_days: Math.max(-2, (Number(prev.hijri_offset_days) || 0) - 1)
                    }))}
                  >
                    -1
                  </Button>
                  <Input
                    type="number"
                    data-testid="hijri-offset-input"
                    value={dateDisplaySettings.hijri_offset_days}
                    onChange={(e) => {
                      const v = parseInt(e.target.value);
                      const safe = Number.isNaN(v) ? 0 : Math.max(-2, Math.min(2, v));
                      setDateDisplaySettings(prev => ({ ...prev, hijri_offset_days: safe }));
                    }}
                    min={-2}
                    max={2}
                    className="bg-secondary/50 border-white/10 w-24 text-center"
                  />
                  <Button
                    type="button"
                    data-testid="hijri-offset-plus"
                    variant="outline"
                    className="border-white/10"
                    onClick={() => setDateDisplaySettings(prev => ({
                      ...prev,
                      hijri_offset_days: Math.min(2, (Number(prev.hijri_offset_days) || 0) + 1)
                    }))}
                  >
                    +1
                  </Button>
                  <span className="text-xs text-muted-foreground">يوم</span>
                </div>
              </div>
            )}

            {/* Live Preview */}
            <div className="p-3 bg-secondary/40 rounded-lg border border-white/5">
              <p className="text-xs text-muted-foreground mb-2">معاينة مباشرة:</p>
              <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="date-display-preview">
                {dateDisplaySettings.show_gregorian && (
                  <span className="text-muted-foreground" dir="ltr">
                    {(() => {
                      const iso = new Intl.DateTimeFormat('en-CA', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        timeZone: 'Asia/Dubai'
                      }).format(new Date());
                      const [y, m, d] = iso.split('-');
                      return `${d}/${m}/${y}`;
                    })()}
                  </span>
                )}
                {dateDisplaySettings.show_gregorian && dateDisplaySettings.show_hijri && (
                  <span className="text-muted-foreground/40">•</span>
                )}
                {dateDisplaySettings.show_hijri && (
                  <span className="text-amber-400 font-medium">
                    {(() => {
                      const s = new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
                        day: '2-digit', month: 'long', year: 'numeric',
                        timeZone: 'Asia/Dubai'
                      }).format(new Date(Date.now() + (Number(dateDisplaySettings.hijri_offset_days) || 0) * 86400000));
                      return /هـ\s*$/.test(s) ? s : `${s} هـ`;
                    })()}
                  </span>
                )}
                {!dateDisplaySettings.show_gregorian && !dateDisplaySettings.show_hijri && (
                  <span className="text-muted-foreground italic">لن يظهر أي تاريخ بجانب الساعة</span>
                )}
              </div>
            </div>
          </div>

          <Button
            data-testid="save-date-display-settings-btn"
            onClick={handleSaveDateDisplaySettings}
            disabled={dateDisplaySaving}
            className="bg-primary text-primary-foreground"
          >
            {dateDisplaySaving ? 'جارٍ الحفظ...' : 'حفظ إعدادات عرض التاريخ'}
          </Button>
        </div>
      </div>

      {/* Biometric / Face ID Settings */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6 relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <Fingerprint className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">تسجيل الدخول بالبصمة / Face ID</h3>
        </div>
        
        <div className="space-y-4">
          {/* Status Info */}
          <div className="p-4 bg-secondary/30 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Smartphone className="w-5 h-5 text-muted-foreground" />
                <span>دعم الجهاز</span>
              </div>
              {biometricStatus.supported ? (
                <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm">مدعوم</span>
              ) : (
                <span className="px-3 py-1 bg-red-500/20 text-red-500 rounded-full text-sm">غير مدعوم</span>
              )}
            </div>
            
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Fingerprint className="w-5 h-5 text-muted-foreground" />
                <span>البصمة / Face ID</span>
              </div>
              {biometricStatus.platformAvailable ? (
                <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm">متوفر</span>
              ) : (
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-sm">غير متوفر على هذا الجهاز</span>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-muted-foreground" />
                <span>حالة التسجيل</span>
              </div>
              {biometricStatus.registered ? (
                <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">مسجّل ✓</span>
              ) : (
                <span className="px-3 py-1 bg-gray-500/20 text-gray-400 rounded-full text-sm">غير مسجّل</span>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 flex-wrap">
            {!biometricStatus.registered ? (
              <Button
                onClick={handleRegisterBiometric}
                disabled={!biometricStatus.supported || !biometricStatus.platformAvailable || biometricLoading}
                className="bg-primary text-primary-foreground"
              >
                {biometricLoading ? (
                  <RefreshCw className="w-4 h-4 me-2 animate-spin" />
                ) : (
                  <Fingerprint className="w-4 h-4 me-2" />
                )}
                تسجيل البصمة / Face ID
              </Button>
            ) : (
              <Button
                onClick={handleDeleteBiometric}
                disabled={biometricLoading}
                variant="outline"
                className="border-red-500/50 text-red-500 hover:bg-red-500/10"
              >
                {biometricLoading ? (
                  <RefreshCw className="w-4 h-4 me-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 me-2" />
                )}
                حذف البصمة المسجلة
              </Button>
            )}
          </div>
          
          {/* Info */}
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong className="text-blue-400">ملاحظة:</strong> عند تسجيل البصمة، ستتمكن من تسجيل الدخول بسرعة باستخدام بصمة الإصبع أو Face ID بدلاً من إدخال كلمة المرور في كل مرة.
            </p>
          </div>
        </div>
      </div>

      {/* App Version & Updates */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6 relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <RefreshCw className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">معلومات التطبيق والتحديثات</h3>
        </div>
        
        <div className="space-y-4">
          {/* Version Info */}
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-primary" />
              <div>
                <span className="text-muted-foreground">الإصدار الحالي:</span>
                <span className="text-primary font-bold ms-2">2.0.0</span>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              آخر تحديث: 11 مارس 2026
            </div>
          </div>

          {/* Auto Update Status */}
          <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-green-500 font-medium">التحديث التلقائي مُفعّل</span>
            </div>
            <span className="text-xs text-muted-foreground">يتم فحص التحديثات كل ساعة</span>
          </div>

          {/* Info */}
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong className="text-blue-400">ملاحظة:</strong> يقوم التطبيق بفحص التحديثات تلقائياً كل ساعة. عند توفر تحديث جديد، ستظهر لك رسالة في أعلى الشاشة لتحديث التطبيق.
            </p>
          </div>
        </div>
      </div>

      {/* Google Drive Backup */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6 relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <CloudUpload className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">Google Drive {t('autoBackup')}</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg flex-wrap gap-4">
            <div className="flex items-center gap-3">
              {driveStatus.connected ? (
                <>
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-green-500 font-medium">{t('driveConnected')}</span>
                </>
              ) : (
                <>
                  <X className="w-5 h-5 text-red-500" />
                  <span className="text-red-500 font-medium">{t('driveNotConnected')}</span>
                </>
              )}
            </div>
            
            {!driveStatus.connected && (
              <Button
                data-testid="connect-drive-btn"
                onClick={handleConnectDrive}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {t('connectDrive')}
              </Button>
            )}
          </div>

          {driveStatus.connected && (
            <>
              {driveStatus.last_backup && (
                <p className="text-sm text-muted-foreground">
                  {t('lastBackup')}: {new Date(driveStatus.last_backup).toLocaleString('ar-SA')}
                </p>
              )}
              
              <Button
                data-testid="backup-now-btn"
                onClick={handleBackup}
                disabled={backupLoading}
                className="bg-primary text-primary-foreground"
              >
                {backupLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 me-2 animate-spin" />
                    جاري النسخ...
                  </>
                ) : (
                  <>
                    <CloudUpload className="w-4 h-4 me-2" />
                    {t('backupNow')}
                  </>
                )}
              </Button>
            </>
          )}
        </div>

        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-sm text-blue-400">
            ℹ️ لربط Google Drive، ستحتاج إلى إعداد Google Cloud OAuth credentials. يمكنك إضافة Client ID و Client Secret في ملف .env للخادم.
          </p>
        </div>
      </div>

      {/* Social Media Links */}
      <div className="bg-card border border-white/10 rounded-xl shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="w-6 h-6 text-primary" />
          <h3 className="text-xl font-semibold text-white">
            {language === 'ar' ? 'روابط التواصل الاجتماعي' : 'Social Media Links'}
          </h3>
        </div>
        
        <p className="text-muted-foreground mb-6">
          {language === 'ar' 
            ? 'أضف روابط حسابات التواصل الاجتماعي لعرضها على الصفحة الرئيسية'
            : 'Add social media links to display on the homepage'
          }
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-2">Instagram</label>
            <Input
              type="url"
              value={socialMediaLinks.instagram || ''}
              onChange={(e) => setSocialMediaLinks({...socialMediaLinks, instagram: e.target.value})}
              placeholder="https://instagram.com/..."
              className="bg-secondary/50 border-white/10"
              dir="ltr"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">TikTok</label>
            <Input
              type="url"
              value={socialMediaLinks.tiktok || ''}
              onChange={(e) => setSocialMediaLinks({...socialMediaLinks, tiktok: e.target.value})}
              placeholder="https://tiktok.com/@..."
              className="bg-secondary/50 border-white/10"
              dir="ltr"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Snapchat</label>
            <Input
              type="url"
              value={socialMediaLinks.snapchat || ''}
              onChange={(e) => setSocialMediaLinks({...socialMediaLinks, snapchat: e.target.value})}
              placeholder="https://snapchat.com/add/..."
              className="bg-secondary/50 border-white/10"
              dir="ltr"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Facebook</label>
            <Input
              type="url"
              value={socialMediaLinks.facebook || ''}
              onChange={(e) => setSocialMediaLinks({...socialMediaLinks, facebook: e.target.value})}
              placeholder="https://facebook.com/..."
              className="bg-secondary/50 border-white/10"
              dir="ltr"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">X (Twitter)</label>
            <Input
              type="url"
              value={socialMediaLinks.twitter || ''}
              onChange={(e) => setSocialMediaLinks({...socialMediaLinks, twitter: e.target.value})}
              placeholder="https://x.com/..."
              className="bg-secondary/50 border-white/10"
              dir="ltr"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">YouTube</label>
            <Input
              type="url"
              value={socialMediaLinks.youtube || ''}
              onChange={(e) => setSocialMediaLinks({...socialMediaLinks, youtube: e.target.value})}
              placeholder="https://youtube.com/@..."
              className="bg-secondary/50 border-white/10"
              dir="ltr"
            />
          </div>
        </div>

        <Button onClick={handleSaveSocialMedia} className="mt-6 bg-primary">
          <Check className="w-4 h-4 me-2" />
          {language === 'ar' ? 'حفظ الروابط' : 'Save Links'}
        </Button>
      </div>

      {/* Deposit Settings (العربون) */}
      <div className="bg-card border border-white/10 rounded-xl shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <DirhamIconColored className="w-6 h-6" />
          <h3 className="text-xl font-semibold text-white">
            {language === 'ar' ? 'إعدادات العربون' : 'Deposit Settings'}
          </h3>
        </div>
        
        <p className="text-muted-foreground mb-6">
          {language === 'ar' 
            ? 'تحديد قيمة العربون المطلوب عند الحجز. العربون يُخصم من إجمالي الخدمة ولا يُسترد.'
            : 'Set the deposit amount required for booking. Deposit is deducted from total and non-refundable.'
          }
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'ar' ? 'نوع العربون' : 'Deposit Type'}
            </label>
            <select
              value={depositSettings.deposit_type}
              onChange={(e) => setDepositSettings({...depositSettings, deposit_type: e.target.value})}
              className="w-full bg-secondary/50 border border-white/10 rounded-lg px-4 py-2 text-white"
            >
              <option value="fixed">{language === 'ar' ? 'مبلغ ثابت' : 'Fixed Amount'}</option>
              <option value="percentage">{language === 'ar' ? 'نسبة من السعر' : 'Percentage of Price'}</option>
            </select>
          </div>
          
          {depositSettings.deposit_type === 'fixed' ? (
            <div>
              <label className="block text-sm font-medium mb-2">
                                {language === 'ar' ? 'قيمة العربون' : 'Deposit Amount'} <DirhamIconColored className="w-3 h-3 inline" />
              </label>
              <Input
                type="number"
                value={depositSettings.deposit_amount}
                onChange={(e) => setDepositSettings({...depositSettings, deposit_amount: parseFloat(e.target.value) || 0})}
                min="0"
                step="5"
                className="bg-secondary/50 border-white/10"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'ar' ? 'نسبة العربون (%)' : 'Deposit Percentage (%)'}
              </label>
              <Input
                type="number"
                value={depositSettings.deposit_percentage}
                onChange={(e) => setDepositSettings({...depositSettings, deposit_percentage: parseFloat(e.target.value) || 0})}
                min="0"
                max="100"
                step="5"
                className="bg-secondary/50 border-white/10"
              />
            </div>
          )}
        </div>

        <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
          <p className="text-sm text-green-400">
            ✓ {language === 'ar' ? 'العربون يُخصم من إجمالي سعر الخدمة ولا يُسترد' : 'Deposit is deducted from total and non-refundable'}
          </p>
        </div>

        {/* Pending Proposals for Admin */}
        {isAdmin() && depositProposals.length > 0 && (
          <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <h4 className="font-medium text-yellow-400 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              اقتراحات معلقة ({depositProposals.length})
            </h4>
            <div className="space-y-3">
              {depositProposals.map(proposal => (
                <div key={proposal.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <div>
                    <p className="text-sm">
                      <strong>{proposal.created_by_name}</strong> يقترح: 
                                            {proposal.deposit_type === 'fixed' 
                        ? <><PriceWithDirham amount={proposal.deposit_amount} /> (مبلغ ثابت)</> 
                        : ` ${proposal.deposit_percentage}% (نسبة)`
                      }
                    </p>
                    {proposal.note && <p className="text-xs text-muted-foreground mt-1">{proposal.note}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApproveDepositProposal(proposal.id)}
                      className="bg-green-500 hover:bg-green-600"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRejectDepositProposal(proposal.id)}
                      className="border-red-500/50 text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button onClick={handleSaveDepositSettings} className="mt-6 bg-primary">
          <Check className="w-4 h-4 me-2" />
          {isAdmin() 
            ? (language === 'ar' ? 'حفظ إعدادات العربون' : 'Save Deposit Settings')
            : (language === 'ar' ? 'إرسال اقتراح للمدير' : 'Submit Proposal to Admin')
          }
        </Button>
      </div>

      {/* Danger Zone - Delete Data */}
      <div className="bg-card border border-red-500/30 rounded-xl shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <AlertTriangle className="w-6 h-6 text-red-500" />
          <h3 className="text-xl font-semibold text-red-500">منطقة الخطر</h3>
        </div>
        
        <p className="text-muted-foreground mb-6">
          ⚠️ تحذير: هذه العمليات لا يمكن التراجع عنها. سيتم طلب رمز تحقق OTP للتأكيد.
        </p>

        <div className="space-y-4">
          {/* Delete All Revenues */}
          <div className="flex items-center justify-between p-4 bg-red-500/5 rounded-lg border border-red-500/10">
            <div>
              <p className="font-medium">حذف جميع الإيرادات</p>
              <p className="text-sm text-muted-foreground">سيتم حذف جميع سجلات الإيرادات</p>
            </div>
            <Button
              onClick={() => {
                setDeleteDialogOpen(true);
                handleRequestOTP('delete_all_revenues');
              }}
              variant="outline"
              className="border-red-500/50 text-red-500 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 me-2" />
              حذف الإيرادات
            </Button>
          </div>

          {/* Delete All Barbers */}
          <div className="flex items-center justify-between p-4 bg-red-500/5 rounded-lg border border-red-500/10">
            <div>
              <p className="font-medium">حذف جميع الحلاقين</p>
              <p className="text-sm text-muted-foreground">سيتم حذف الحلاقين وجميع إيراداتهم</p>
            </div>
            <Button
              onClick={() => {
                setDeleteDialogOpen(true);
                handleRequestOTP('delete_all_barbers');
              }}
              variant="outline"
              className="border-red-500/50 text-red-500 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 me-2" />
              حذف الحلاقين
            </Button>
          </div>

          {/* Delete All Data */}
          <div className="flex items-center justify-between p-4 bg-red-500/10 rounded-lg border border-red-500/20">
            <div>
              <p className="font-medium text-red-500">حذف جميع البيانات</p>
              <p className="text-sm text-muted-foreground">سيتم حذف كل شيء ماعدا حسابات المستخدمين</p>
            </div>
            <Button
              onClick={() => {
                setDeleteDialogOpen(true);
                handleRequestOTP('delete_all_data');
              }}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              <Trash2 className="w-4 h-4 me-2" />
              حذف الكل
            </Button>
          </div>
        </div>

        {/* OTP Verification Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="bg-card border-red-500/30">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-500">
                <Shield className="w-5 h-5" />
                تأكيد الحذف برمز OTP
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                <p className="text-sm font-medium text-red-500 mb-2">
                  العملية: {getActionLabel(deleteAction)}
                </p>
                <p className="text-xs text-muted-foreground">
                  هذه العملية لا يمكن التراجع عنها!
                </p>
              </div>

              {otpData && (
                <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                  <p className="text-sm mb-2">رمز التحقق OTP:</p>
                  <p className="text-3xl font-bold text-primary text-center tracking-widest">
                    {otpData.otp}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    صالح لمدة {otpData.expires_in_minutes} دقائق
                  </p>
                  <p className="text-xs text-yellow-500 mt-2">
                    {otpData.note}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">أدخل رمز التحقق:</label>
                <Input
                  type="text"
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="bg-secondary/50 border-white/10 text-center text-2xl tracking-widest"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setDeleteDialogOpen(false);
                    setOtpData(null);
                    setOtpInput('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  إلغاء
                </Button>
                <Button
                  onClick={handleVerifyAndDelete}
                  disabled={deleteLoading || otpInput.length !== 6}
                  className="flex-1 bg-red-500 text-white hover:bg-red-600"
                >
                  {deleteLoading ? 'جاري الحذف...' : 'تأكيد الحذف'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Settings;
